import React, { type ComponentType, type ReactNode, useCallback, useMemo, useState } from 'react';

import type { CreatePanel, PanelComponent } from './types';

type ProviderComponent = ComponentType<{ children: ReactNode }>;

type ReturnType = { Panel: PanelComponent; Provider: ProviderComponent };

export function usePanelContext({ Context }: CreatePanel, initialState?: ReactNode): ReturnType {
	const [panel, setPanel] = useState<ReactNode>(initialState);

	const Panel = useMemo<PanelComponent>(() => {
		const PanelComponent = () => panel ?? null;
		return PanelComponent;
	}, [panel]);

	const handleSetPanel = useCallback((panel: ReactNode) => {
		setPanel(panel);
	}, []);

	const value = useMemo(
		() => ({
			setPanel: handleSetPanel,
		}),
		[handleSetPanel],
	);

	const Provider = useMemo<ComponentType<{ children: ReactNode }>>(
		() =>
			({ children }) => {
				return <Context.Provider value={value}>{children}</Context.Provider>;
			},
		[Context, value],
	);

	return useMemo<ReturnType>(() => ({ Panel, Provider }), [Panel, Provider]);
}
